import queryString from "query-string";
import {
  AccountabilityExpense,
  ApprovalType,
  ExpensePaymentMethod,
  JudgementStatus,
  PaginatedResource,
  TravelerAdvance,
  VoucherOcr,
} from "~/application/types";
import { OrderExpenseReport } from "~/application/types/entities/OrderExpenseReport";
import { DateUtils } from "~/application/utils";
import { DateFormats } from "~/application/utils/date-functions";
import { FormDataBuilder } from "~/application/utils/object-functions";
import { api } from "~/infrastructure/api";
import {
  AccountabilityExpenseDTO,
  TravelerAdvanceDTO,
  VoucherOcrDTO,
} from "~/infrastructure/api/dtos";
import { OrderExpenseReportDTO } from "~/infrastructure/api/dtos/OrderExpenseReportDTO";
import {
  mapAccountabilityExpenseDTO,
  mapTravelerAdvanceDTO,
  mapVoucherOcrDTO,
} from "~/infrastructure/api/mappers";
import { mapOrderViolatedPolicies } from "~/infrastructure/api/mappers/mapOrderDTO";
import { toDate } from "~/utils/date.utils";
import { parseTravelerAdvanceDTO } from "../OrderAdvance/OrderAdvanceService";
import {
  CreateAccountabilityReport,
  IAccountabilityExpenseService,
  ICreateAccountabilityExpenseParams,
  IFindAccountabilityExpenseParams,
  IFindAccountabilityExpenseResult,
  IFindAllExpensesParams,
  IFindExpenseByAdvanceOrderResult,
  IProveTransactionParams,
  ProcessVoucherOcrLinkResult,
  JudgeExpenseReportPayload,
  RejectedItemPayload,
  CreateCardExpenseReportParams,
} from "./IAccountabilityExpenseService";
import { getAuthToken } from "~/utils/auth.utils";
import { downloadBlob } from "~/utils";

export type CreateOrderExpenseReport = Omit<
  OrderExpenseReport,
  "accountabilityExpenses" | "advanceOrders"
> & {
  order: {
    uuid: string;
    orderNumber: string;
  };
};

export type CreateOrderExpenseReportDTO = Omit<
  OrderExpenseReportDTO,
  "advance_orders" | "accountability_expenses"
> & {
  order: {
    uuid: string;
    order_number: string;
  };
};
interface IFindAccountabilityExpenseResponse {
  summary: {
    advances: number;
    expenses: number;
    refund: number;
  };
  data: AccountabilityExpenseDTO[];
}

type ApprovalRequest =
  IFindExpenseByAdvanceOrderResult["data"][0]["advanceOrder"]["approvalRequest"];

function parseAdvanceOrder(
  advanceOrder: TravelerAdvanceDTO & {
    approval_request?: {
      date: string;
      advance_order_uuid: string;
      approved: boolean;
      approval_model: { approval_type: ApprovalType; active: boolean };
    };
  },
): IFindExpenseByAdvanceOrderResult["data"][0]["advanceOrder"] {
  let approvalRequest: ApprovalRequest = {} as ApprovalRequest;

  if (advanceOrder.approval_request) {
    approvalRequest = {
      date: advanceOrder.approval_request.date
        ? DateUtils.toDate(advanceOrder.approval_request?.date)
        : undefined,
      advanceOrderId: advanceOrder.approval_request.advance_order_uuid as string,
      isApproved: advanceOrder.approval_request.approved,
      approvalModel: {
        approvalType: advanceOrder.approval_request.approval_model.approval_type as ApprovalType,
        isActive: advanceOrder.approval_request.approval_model.active,
      },
    };
  }

  return {
    ...parseTravelerAdvanceDTO(advanceOrder as TravelerAdvanceDTO),
    approvalRequest: { ...approvalRequest },
  };
}

function createBody({
  employeeReturn = false,
  customerReturn = false,
  ...data
}: AccountabilityExpense): FormData {
  const formDataBuilder = new FormDataBuilder({
    expense_type_uuid: data.expenseType?.uuid,
    expense_date: data.expenseDate
      ? DateUtils.format(data.expenseDate as Date, DateFormats.DATE_ISO)
      : undefined,
    company: data.company,
    value: data.value?.toString(),
    justification: data.justification,
    obs: data.obs,
    voucher_image:
      (data.voucherImage?.name as string) !== "Comprovante existente"
        ? data.voucherImage
        : undefined,
    cost_center_uuid: data.costCenter?.uuid,
    project_uuid: data.project?.uuid,
    phase_uuid: data.phase?.uuid,
    payment_method: data.paymentMethod,
    refundable: data.isRefundable,
    car_model: data.carModel,
    license_plate: data.licensePlate,
    kilometer: data.kilometer?.toString(),
    odometer_image: data.odometerImage,
    employee_return: employeeReturn,
    customer_return: customerReturn,
    size: data.voucherImage?.size?.toString(),
  });

  return formDataBuilder.getFormData();
}

function createKmExpenseBody(data: AccountabilityExpense) {
  return {
    expense_date: data.expenseDate
      ? DateUtils.format(data.expenseDate as Date, DateFormats.DATE_ISO)
      : undefined,
    justification: data.justification,
    cost_center_uuid: data.costCenter?.uuid,
    project_uuid: data.project?.uuid,
    phase_uuid: data.phase?.uuid,
    refundable: data.isRefundable ? "true" : "false",
    obs: data.obs,
    routes: data.routeDetails?.routes?.map((route) => ({
      from: {
        place_id: route.from.placeId,
        address: route.from.address,
        latitude: route.from.latitude,
        longitude: route.from.longitude,
      },
      to: {
        place_id: route.to.placeId,
        address: route.to.address,
        latitude: route.to.latitude,
        longitude: route.to.longitude,
      },
      polyline: route.polyline,
      distance: {
        meters: route.distanceInMeters,
      },
      duration: {
        seconds: route.durationInSeconds,
      },
      total_value: {
        brl: route.totalValueInBRL,
      },
    })),
    distance: {
      meters: data.routeDetails?.distanceInMeters,
    },
    duration: {
      seconds: data.routeDetails?.durationInSeconds,
    },
    total_value: {
      brl: data.routeDetails?.totalValueInBRL,
    },
  };
}

export class AccountabilityExpenseService implements IAccountabilityExpenseService {
  private headers = {
    "Content-Type": "multipart/form-data",
  };

  async find({
    orderId,
    travelerId,
    expenseDate,
    expenseTypeId,
  }: IFindAccountabilityExpenseParams): Promise<IFindAccountabilityExpenseResult> {
    const url = queryString.stringifyUrl({
      url: `/orders/${orderId}/travelers/expenses`,
      query: {
        customer_employee_uuid: travelerId,
        expense_type_uuid: expenseTypeId,
        expense_date: expenseDate,
      },
    });

    return await api.get<IFindAccountabilityExpenseResponse>(url).then(({ data: response }) => ({
      summary: {
        advances: response.summary?.advances,
        expenses: response.summary?.expenses,
        refundable: response.summary?.refund,
      },
      data: response.data.length ? response.data.map(mapAccountabilityExpenseDTO) : [],
    }));
  }

  async findByAdvanceOrder(advanceOrderId: string): Promise<IFindExpenseByAdvanceOrderResult> {
    const url = `/orders/advance-orders/${advanceOrderId}/expenses`;

    return await api
      .get<{
        expenses: AccountabilityExpenseDTO[];
        advance_order: TravelerAdvanceDTO;
        approvers: {
          uuid: string;
          name: string;
          self_approver: boolean;
          approval_status?: JudgementStatus;
        }[];
      }>(url)
      .then(({ data: response }) => ({
        data: [
          {
            expenses: response.expenses.map(mapAccountabilityExpenseDTO),
            advanceOrder: parseAdvanceOrder(response.advance_order),
            approvers: response.approvers.map(({ uuid, name, self_approver, approval_status }) => ({
              id: uuid,
              name,
              isSelfApprover: self_approver,
              approvalStatus: approval_status,
            })),
          },
        ],
      }));
  }

  async judgeExpenseReport({
    expenseReportId,
    reasonRejected,
    obs,
    approveRejectedItems,
    partiallyApprovedItems,
    rejectedItems,
  }: JudgeExpenseReportPayload): Promise<void> {
    const url = `/expense-reports/${expenseReportId}/judge`;
    const body = {
      reason_rejected: reasonRejected,
      obs,
      approve_rejected_items: approveRejectedItems,
      partially_approved_items: partiallyApprovedItems?.map((i) => ({
        accountability_expense_uuid: i.expenseId,
        approved_amount: i.approvedAmount,
        justification: i.justification,
      })),
      rejected_items: rejectedItems?.map((i) => ({
        accountability_expense_uuid: i.expenseId,
        justification: i.justification,
      })),
    };

    return await api.post(url, body).then(({ data }) => data);
  }

  async findAll({
    customerId,
    statuses,
    search,
    page,
    limit,
    withExpenseReport,
    customerReturn,
    type,
    cardId,
  }: IFindAllExpensesParams): Promise<PaginatedResource<AccountabilityExpense[]>> {
    const url = queryString.stringifyUrl(
      {
        url: `/customers/${customerId}/expenses`,
        query: {
          statuses: statuses,
          search,
          page,
          limit,
          with_expense_report: withExpenseReport,
          return: customerReturn,
          type,
          card_uuid: cardId,
        },
      },
      { arrayFormat: "bracket" },
    );

    return await api
      .get<PaginatedResource<AccountabilityExpenseDTO[]>>(url)
      .then(({ data: response }) => {
        return {
          data: response.data.map(mapAccountabilityExpenseDTO),
          links: response.links,
          meta: response.meta,
        };
      });
  }

  async findById(expenseId: string): Promise<AccountabilityExpense> {
    const url = `/employees/expenses/${expenseId}`;
    return await api
      .get<AccountabilityExpenseDTO>(url)
      .then(({ data }) => mapAccountabilityExpenseDTO(data));
  }

  async createReport(data: CreateAccountabilityReport): Promise<CreateOrderExpenseReport> {
    const url = `/expense-reports`;
    const body = {
      description: data.description,
      name: data.name,
      report_date: DateUtils.format(data.reportDate as Date, DateFormats.DATE_ISO),
      expense_uuids: data.expenses?.map((expense) => expense.uuid) ?? [],
      advance_order_uuids: data.advanceOrders?.map((advance) => advance.uuid) ?? [],
      customer_employee_uuid: data.customerEmployeeId,
    };

    return await api
      .post<{ data: CreateOrderExpenseReportDTO }>(url, body)
      .then(({ data: { data } }) => ({
        name: data.name,
        uuid: data.uuid,
        description: data.description,
        reportDate: toDate(data.report_date),
        createdAt: toDate(data.created_at),
        order: {
          uuid: data.order?.uuid,
          orderNumber: data.order?.order_number,
        },
        refundAmount: parseFloat(data.refund_amount),
        reportAmount: parseFloat(data.report_amount),
        returnAmount: parseFloat(data.return_amount),
        status: data.status,
        updatedAt: toDate(data.updated_at),
        violatedPolicies: mapOrderViolatedPolicies(data.violated_policies),
      }));
  }

  async updateReport(
    expenseReportId: string,
    data: CreateAccountabilityReport,
  ): Promise<CreateOrderExpenseReport> {
    const url = `/expense-reports/${expenseReportId}`;
    const body = {
      report_date: DateUtils.format(data.reportDate as Date, DateFormats.DATE_ISO),
      description: data.description,
      name: data.name,
      expense_uuids: data.expenses?.map((expense) => expense.uuid) ?? [],
      advance_order_uuids: data.advanceOrders?.map((advance) => advance.uuid) ?? [],
      customer_employee_uuid: data.customerEmployeeId,
    };

    return await api
      .put<{ data: CreateOrderExpenseReportDTO }>(url, body)
      .then(({ data: { data } }) => ({
        name: data.name,
        uuid: data.uuid,
        description: data.description,
        reportDate: toDate(data.report_date),
        createdAt: toDate(data.created_at),
        order: {
          uuid: data.order?.uuid,
          orderNumber: data.order?.order_number,
        },
        refundAmount: parseFloat(data.refund_amount),
        reportAmount: parseFloat(data.report_amount),
        returnAmount: parseFloat(data.return_amount),
        status: data.status,
        updatedAt: toDate(data.updated_at),
        violatedPolicies: mapOrderViolatedPolicies(data.violated_policies),
      }));
  }

  async approvalRequestReport(expenseReportId: string, data?: RejectedItemPayload[]) {
    const url = `/expense-reports/${expenseReportId}/request-approval`;
    const body = {
      justifications: data?.map((item) => ({
        accountability_expense_uuid: item.expenseId,
        reason: item.justification,
      })),
    };
    return await api.post(url, body).then(({ data }) => data);
  }

  async judge(expenseReportId: string, accountabilityExpenseId: string, status: JudgementStatus) {
    const url = `/expense-reports/${expenseReportId}/judge`;
    const body = {
      accountability_expense_uuid: accountabilityExpenseId,
      status,
    };
    return await api.post(url, body).then(({ data }) => data);
  }

  async findByAdvance({
    advanceOrderId,
    travelerId,
  }: {
    advanceOrderId: string;
    travelerId: string;
  }): Promise<TravelerAdvance> {
    const url = `orders/${advanceOrderId}/travelers/${travelerId}/advance-orders`;

    return await api.get(url).then(({ data: response }) => mapTravelerAdvanceDTO(response));
  }

  async findPaymentMethods(): Promise<{ label: string; value: ExpensePaymentMethod }[]> {
    const url = `/expense-payment-methods`;

    return await api
      .get<{ data: { label: string; value: ExpensePaymentMethod }[] }>(url)
      .then(({ data }) => data.data);
  }

  async create({
    travelerId,
    ...data
  }: ICreateAccountabilityExpenseParams): Promise<AccountabilityExpense> {
    const url = `/employees/${travelerId}/expenses`;

    return await api
      .post<{
        data: AccountabilityExpenseDTO;
      }>(url, createBody(data as AccountabilityExpense), this.headers)
      .then(({ data: { data } }) => mapAccountabilityExpenseDTO(data));
  }

  async createKmExpense({
    travelerId,
    ...data
  }: ICreateAccountabilityExpenseParams): Promise<AccountabilityExpense> {
    const url = `/employees/${travelerId}/expenses/km`;
    const body = createKmExpenseBody(data as AccountabilityExpense);

    return api.post(url, body).then(({ data: { data } }) => mapAccountabilityExpenseDTO(data));
  }

  async updateKmExpense(data: AccountabilityExpense): Promise<AccountabilityExpense> {
    const url = `/employees/expenses/km/${data.uuid}`;
    const body = createKmExpenseBody(data as AccountabilityExpense);

    return api.put(url, body).then(({ data: { data } }) => mapAccountabilityExpenseDTO(data));
  }

  async update(data: AccountabilityExpense): Promise<AccountabilityExpense> {
    const url = `/employees/expenses/${data.uuid}`;
    return await api
      .post<AccountabilityExpenseDTO>(url, createBody(data), this.headers)
      .then(({ data }) => mapAccountabilityExpenseDTO(data));
  }

  async updateById(data: AccountabilityExpense): Promise<AccountabilityExpense> {
    const url = `/orders/travelers/expenses/${data.uuid}`;
    return await api
      .post<AccountabilityExpenseDTO>(url, createBody(data), this.headers)
      .then(({ data }) => mapAccountabilityExpenseDTO(data));
  }

  async deleteById(data: AccountabilityExpense): Promise<void> {
    return await api.delete(`/employees/expenses/${data.uuid}`).then(({ data }) => data);
  }

  async processVoucherOcrLinks(): Promise<ProcessVoucherOcrLinkResult> {
    return await api
      .post<{
        links: string[];
        wait_time: number;
      }>("/voucher-ocr/links", this.headers)
      .then(({ data }) => ({
        links: data.links,
        waitTime: data.wait_time,
      }));
  }

  async processVoucherOcr(link: string, file: File, timeoutMs?: number): Promise<VoucherOcr> {
    const formData = new FormData();

    formData.append("voucher_image", file);

    return await api
      .post<VoucherOcrDTO>(link, formData, this.headers, { timeoutMs })
      .then(({ data }) => mapVoucherOcrDTO(data));
  }

  async removeExpensesFromReport(expenseReportId: string, expenseUuids: string[]): Promise<void> {
    const url = `/expense-reports/${expenseReportId}/remove-expenses`;
    const body = {
      expense_uuids: expenseUuids,
    };
    await api.post(url, body);
  }

  async deleteReport(expenseReportId: string): Promise<void> {
    await api.delete(`/expense-reports/${expenseReportId}`);
  }

  async sendVoucher(data: { voucher: File }): Promise<VoucherOcr> {
    const formData = new FormDataBuilder({
      voucher_image: data.voucher,
    }).getFormData();

    return await api.post<VoucherOcrDTO>(`/voucher-ocr`, formData).then(({ data }) =>
      mapVoucherOcrDTO({
        voucher_ocr: data.voucher_ocr,
        message: data.message,
      }),
    );
  }

  async getVouchersList(): Promise<VoucherOcr[]> {
    return await api
      .get<{ data: VoucherOcrDTO[] }>(`/voucher-ocr`)
      .then(({ data }) => data.data.map((item) => mapVoucherOcrDTO(item)));
  }

  async downloadExpenseReportPDF(
    expenseReport: OrderExpenseReport,
    fileName?: string,
  ): Promise<void> {
    const url = `${api.baseUrl}/expense-reports/${expenseReport.uuid}/pdf`;

    return fetch(url, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
    })
      .then(async (response) => {
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message);
        }

        return response.blob();
      })
      .then((blob) =>
        downloadBlob(blob, fileName || `relatorio_prestacao_de_contas_${expenseReport.uuid}.pdf`),
      );
  }

  async proveTransaction(data: IProveTransactionParams): Promise<void> {
    const { expenseId, expenseTypeId, receipt, costCenterId, projectId, phaseId } = data;

    const formData = new FormDataBuilder({
      expense_type_uuid: expenseTypeId,
      document: (receipt?.name as string) !== "Comprovante existente" ? receipt : undefined,
      cost_center_uuid: costCenterId,
      project_uuid: projectId,
      phase_uuid: phaseId,
    }).getFormData();

    await api.post(
      `/expenses/${expenseId}/card-transactions/prove-expense`,
      formData,
      this.headers,
    );
  }

  async createCardExpenseReport(
    data: CreateCardExpenseReportParams,
  ): Promise<CreateOrderExpenseReport> {
    const { cardId, name, description } = data;
    const url = `/customer-employees/cards/${cardId}/expense-report`;
    const body = {
      name,
      description,
    };

    return await api
      .post<{ data: CreateOrderExpenseReportDTO }>(url, body)
      .then(({ data: { data } }) => ({
        name: data.name,
        uuid: data.uuid,
        description: data.description,
        reportDate: toDate(data.report_date),
        createdAt: toDate(data.created_at),
        order: {
          uuid: data.order?.uuid,
          orderNumber: data.order?.order_number,
        },
        refundAmount: parseFloat(data.refund_amount),
        reportAmount: parseFloat(data.report_amount),
        returnAmount: parseFloat(data.return_amount),
        status: data.status,
        updatedAt: toDate(data.updated_at),
        violatedPolicies: mapOrderViolatedPolicies(data.violated_policies),
      }));
  }
}
